import { useEffect, useState } from "react";
import { http } from "../api";
import { CheckCircle2, Sparkles, MapPin, Calendar, Wallet, Link2, Check } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

const fmtBudget = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return "";
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)} K`;
  return `₹${v}`;
};

export default function PublicWardWall() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    document.title = "Ward 7 · Community Wall";
    http.get("/public/ward-wall?limit=30")
      .then((r) => setData(r.data))
      .catch(() => setErr("Unable to load the community wall. Please try again shortly."));
  }, []);

  if (err) return <Shell><ErrorState text={err} /></Shell>;
  if (!data) return <Shell><p style={{ padding: 40, textAlign: "center" }}>Loading Ward 7 progress…</p></Shell>;

  const solved = data.solved_complaints || [];
  const fulfilled = data.fulfilled_promises || [];
  const showSolved = tab === "all" || tab === "solved";
  const showPromises = tab === "all" || tab === "promises";

  return (
    <Shell>
      <header className="pww-hero" data-testid="pww-hero">
        <span className="pww-eyebrow">CONSTITUENCY 84 · NABADWIP · WARD 7</span>
        <h1>Progress you can see<span className="pww-dot">.</span></h1>
        <p className="pww-sub">
          A transparent record of resident issues resolved and public promises delivered in Ward 7. Updated live.
        </p>
        <div className="pww-stats">
          <div className="pww-stat" data-testid="pww-stat-solved">
            <b>{data.totals?.solved ?? solved.length}</b><span>issues resolved</span>
          </div>
          <div className="pww-stat" data-testid="pww-stat-fulfilled">
            <b>{data.totals?.fulfilled ?? fulfilled.length}</b><span>promises delivered</span>
          </div>
          <div className="pww-stat">
            <b>7</b><span>ward · 24 booths</span>
          </div>
        </div>
        <ShareBar totals={data.totals} />
      </header>

      <div className="pww-tabs" role="tablist">
        <button className={tab === "all" ? "active" : ""} data-testid="pww-tab-all" onClick={() => setTab("all")}>All updates</button>
        <button className={tab === "solved" ? "active" : ""} data-testid="pww-tab-solved" onClick={() => setTab("solved")}>Issues solved</button>
        <button className={tab === "promises" ? "active" : ""} data-testid="pww-tab-promises" onClick={() => setTab("promises")}>Promises delivered</button>
      </div>

      {showSolved && (
        <section className="pww-section" data-testid="pww-solved-section">
          <h2><CheckCircle2 size={18} /> Issues resolved</h2>
          {solved.length === 0 ? (
            <p className="pww-empty">No resolved issues to show yet. Check back soon.</p>
          ) : (
            <div className="pww-grid">
              {solved.map((c, i) => (
                <article className="pww-card solved" key={c.complaint_id || i} data-testid={`pww-solved-${i}`}>
                  <div className="pww-card-head">
                    <span className="pww-chip green">{c.category}</span>
                    <small><MapPin size={11} /> Booth {c.booth}</small>
                  </div>
                  <h3>{c.title}</h3>
                  {c.before_photo_url && c.after_photo_url && (
                    <BeforeAfterSlider
                      beforeUrl={c.before_photo_url}
                      afterUrl={c.after_photo_url}
                      testid={`pww-slider-${i}`}
                    />
                  )}
                  {c.outcome && <p className="pww-outcome">&ldquo;{c.outcome}&rdquo;</p>}
                  <footer>
                    <span>Reported by <b>{c.reported_by}</b></span>
                    <span><Calendar size={11} /> {fmtDate(c.resolved_at)}</span>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {showPromises && (
        <section className="pww-section" data-testid="pww-promises-section">
          <h2><Sparkles size={18} /> Promises delivered</h2>
          {fulfilled.length === 0 ? (
            <p className="pww-empty">No fulfilled promises to show yet.</p>
          ) : (
            <div className="pww-grid">
              {fulfilled.map((p, i) => (
                <article className="pww-card promise" key={p.promise_id || i} data-testid={`pww-promise-${i}`}>
                  <div className="pww-card-head">
                    <span className="pww-chip amber">Promise</span>
                    {p.budget ? <small><Wallet size={11} /> {fmtBudget(p.budget)}</small> : null}
                  </div>
                  <h3>{p.text}</h3>
                  <footer>
                    <span>By <b>{p.made_by}</b></span>
                    <span><Calendar size={11} /> {fmtDate(p.target_date)}</span>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      <footer className="pww-foot" data-testid="pww-footer">
        <span>Ward 7 CRM · Jonogoner Swarthe Ward 7</span>
        <span>Personal details of residents are anonymised on this public wall.</span>
      </footer>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="pww-root">{children}</div>;
}

function BeforeAfterSlider({ beforeUrl, afterUrl, testid }) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const updateFromClientX = (containerEl, clientX) => {
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, raw)));
  };

  const onMouseDown = (e) => {
    setDragging(true);
    updateFromClientX(e.currentTarget, e.clientX);
  };
  const onTouchStart = (e) => {
    setDragging(true);
    if (e.touches[0]) updateFromClientX(e.currentTarget, e.touches[0].clientX);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const container = document.querySelector(`[data-testid="${testid}"]`);
      const cx = e.touches?.[0]?.clientX ?? e.clientX;
      if (typeof cx === "number") updateFromClientX(container, cx);
    };
    const stop = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", stop);
    };
  }, [dragging, testid]);

  return (
    <div
      className="pww-ba"
      data-testid={testid}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      role="slider"
      aria-label="Before and after"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
    >
      <img className="pww-ba-img after" src={afterUrl} alt="After resolution" loading="lazy" draggable={false} />
      <div className="pww-ba-clip" style={{ width: `${pos}%` }}>
        <img className="pww-ba-img before" src={beforeUrl} alt="Before resolution" loading="lazy" draggable={false} style={{ width: `${10000 / Math.max(pos, 0.01)}%` }} />
      </div>
      <span className="pww-ba-tag left">Before</span>
      <span className="pww-ba-tag right">After</span>
      <div className="pww-ba-handle" style={{ left: `${pos}%` }} aria-hidden="true">
        <span />
      </div>
    </div>
  );
}

function ShareBar({ totals }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const solved = totals?.solved ?? 0;
  const fulfilled = totals?.fulfilled ?? 0;
  const msg = `Ward 7 progress update: ${solved} resident issue${solved === 1 ? "" : "s"} resolved and ${fulfilled} promise${fulfilled === 1 ? "" : "s"} delivered. See the live wall:`;
  const enc = encodeURIComponent;
  const links = {
    whatsapp: `https://wa.me/?text=${enc(`${msg} ${url}`)}`,
    x: `https://twitter.com/intent/tweet?text=${enc(msg)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  };
  const openShare = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };
  const copyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };
  return (
    <div className="pww-share" data-testid="pww-share-bar">
      <span className="pww-share-label">Share the good news</span>
      <button className="pww-share-btn wa" data-testid="pww-share-whatsapp" onClick={() => openShare(links.whatsapp)} aria-label="Share on WhatsApp">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.5 3.5A11.9 11.9 0 0 0 12 0C5.4 0 .1 5.3.1 11.9c0 2.1.6 4.2 1.6 6L0 24l6.3-1.7c1.7.9 3.7 1.4 5.7 1.4 6.6 0 11.9-5.3 11.9-11.9 0-3.2-1.2-6.2-3.4-8.3zM12 21.5c-1.8 0-3.6-.5-5.1-1.4l-.4-.2-3.7 1 1-3.6-.2-.4c-1-1.6-1.6-3.5-1.6-5.4C2 6.4 6.4 2 12 2c2.7 0 5.2 1 7.1 2.9 1.9 1.9 2.9 4.4 2.9 7.1 0 5.5-4.4 9.9-10 9.5zm5.4-7.4c-.3-.1-1.8-.9-2.1-1s-.5-.1-.7.2-.8 1-.9 1.2c-.2.2-.3.2-.6 0-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1s0-.4.1-.5c.1-.1.3-.3.4-.5s.2-.3.3-.5c.1-.2 0-.4 0-.5 0-.1-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.8-.7 2-1.5.2-.7.2-1.4.2-1.5-.1-.1-.3-.2-.5-.3z"/></svg>
        WhatsApp
      </button>
      <button className="pww-share-btn x" data-testid="pww-share-x" onClick={() => openShare(links.x)} aria-label="Share on X">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.75l-5.28-6.4L4.94 22H1.68l8.02-9.16L1 2h6.9l4.77 5.86L18.244 2zm-1.18 18h1.87L7.03 4H5.07l12 16z"/></svg>
        X
      </button>
      <button className="pww-share-btn fb" data-testid="pww-share-facebook" onClick={() => openShare(links.facebook)} aria-label="Share on Facebook">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z"/></svg>
        Facebook
      </button>
      <button className="pww-share-btn copy" data-testid="pww-share-copy" onClick={copyLink} aria-label="Copy link">
        {copied ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Copy link</>}
      </button>
    </div>
  );
}

function ErrorState({ text }) {
  return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <h2>Something went wrong</h2>
      <p style={{ color: "#7a8a80" }}>{text}</p>
    </div>
  );
}
